import React, { useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LoginForm from "../components/LoginForm";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import loginBg from "../assets/auth/login-bg.png";
import styles from "./LoginPage.module.css";

const LoginPage = () => {
  const { isAuthenticated, user } = useAuth();

  // Force light theme on this page
  useEffect(() => {
    // Store original theme state
    const hadDarkClass = document.documentElement.classList.contains("dark");
    const savedTheme = localStorage.getItem("theme") || "LIGHT";

    // Force light theme
    document.documentElement.classList.remove("dark");

    // Restore original theme on unmount
    return () => {
      // Check if we should restore dark theme based on saved preference
      if (
        savedTheme === "DARK" ||
        (savedTheme === "SYSTEM" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        document.documentElement.classList.add("dark");
      } else if (hadDarkClass && savedTheme !== "LIGHT") {
        // If it was dark before and not explicitly LIGHT, restore it
        document.documentElement.classList.add("dark");
      }
    };
  }, []);

  // Helper function to normalize role names
  const normalizeRole = (role) => {
    if (!role) return null;
    return role.toLowerCase().replace(/[_\s-]/g, "-");
  };

  // Helper function to check if user is chief-admin
  const isChiefAdmin = (userData) => {
    const userRole = userData?.role_name || userData?.role;
    return normalizeRole(userRole) === "chief-admin";
  };

  // If user is already authenticated, redirect based on role
  if (isAuthenticated && user) {
    // Chief admin goes to /clients, all other users go to /projects
    if (isChiefAdmin(user)) {
      return <Navigate to="/clients" replace />;
    } else {
      return <Navigate to="/projects" replace />;
    }
  } else if (isAuthenticated) {
    // If authenticated but no user data yet, try to get from storage
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      if (storedUser) {
        if (isChiefAdmin(storedUser)) {
          return <Navigate to="/clients" replace />;
        } else {
          return <Navigate to="/projects" replace />;
        }
      }
    } catch (e) {
      // If parsing fails, default to projects
      return <Navigate to="/projects" replace />;
    }
  }

  return (
    <div className={styles.page} data-testid="login-page">
      {/* Floating header */}
      <header className={styles.header}>
        <div className={styles.logoWrap}>
          <img
            src="/testnimbus-logo.png"
            alt="TestNimbus"
            className={styles.logo}
          />
          <span className={styles.pageName}>TestNimbus</span>
        </div>
        <nav className={styles.nav}>
          <DropdownMenu>
            <DropdownMenuTrigger className={styles.dropdownTrigger}>
              Features <ChevronDown className={styles.chevron} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem asChild>
                <Link to="/#test-management">Test Management</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#automation">Automation</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#collaboration">Collaboration</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className={styles.dropdownTrigger}>
              Pricing <ChevronDown className={styles.chevron} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem asChild>
                <Link to="/#pricing-plans">Plans</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#enterprise">Enterprise</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#faq">FAQ</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className={styles.dropdownTrigger}>
              About Us <ChevronDown className={styles.chevron} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem asChild>
                <Link to="/#company">Company</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#team">Team</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#careers">Careers</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger className={styles.dropdownTrigger}>
              Help <ChevronDown className={styles.chevron} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem asChild>
                <Link to="/support">Support</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#documentation">Documentation</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/#contact">Contact</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/register">
            <Button className={styles.signUpBtn}>Sign Up</Button>
          </Link>
        </nav>
      </header>

      <img
        src={loginBg}
        alt="TestNimbus Background Illustration"
        className={styles.bgImage}
        data-testid="login-bg-image"
      />
      <div className={styles.modalWrap}>
        <div
          className={styles.modal}
          data-testid="login-modal"
          style={{ position: "absolute" }}
        >
          <LoginForm />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
